import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CredentialRequired } from 'services/credential.service';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { DomainService, DomainWithDnsInformation } from 'services/domain.service';
import { ListView } from 'modules/list-view';
import { SelectListItemModel } from 'models/SelectList';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { NavigationService } from 'services/navigation.service';
import { CredentialType } from 'enums/CredentialType';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ProductService } from 'services/product.service';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { ProductVariantModel } from 'models/generated/Entities.GormCrm.ProductVariant';
import { BrandService } from 'services/brand.service';
import { FormValidationService } from 'services/form-validation.service';

@Component({
	selector: 'request-registrar-management',
	templateUrl: './request-registrar-management.component.html',
	providers: [CompanyDataProviderFactory, FormValidationService],
	imports: [FormsModule, Layout, ListView, CustomSelectComponent, TranslatePipe]
})
@CredentialRequired(CredentialType.RequestRegistrarManagement)
export class RequestRegistrarManagementComponent {

	@Input()
	set companyId(companyId: string | undefined) {
		if (!companyId) return;
		this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(companyId);
		this.dataProvider.getCompany().then(company => {
			this.company = company;
			this.contacts = [];
			company.companyContacts.forEach(contact => {
				if (contact.contact?.emailAddress != undefined) {
					this.contacts.push(new SelectListItemModel(contact.contact!, contact.contact!.fullName + (contact.contact!.emailAddress ? ' (' + contact.contact!.emailAddress + ')' : '')));
					if (company.primaryContactId === contact.contact?.id) this.selectedContact = contact.contact;
				}
			})
		});
	}

	@Input() registrantManagedDomains: DomainWithDnsInformation[] = [];
	@Input() selectedDomains: DomainWithDnsInformation[] = [];

	dataProvider?: CompanyDataProvider;
	company?: CompanyModel;
	contacts: SelectListItemModel[] = [];
	selectedContact?: ContactModel;
	availableVariants: SelectListItemModel[] = [];
	selectedVariant?: ProductVariantModel;
	brandId?: string;
	brands: SelectListItemModel[];

	private readonly navigation = inject(NavigationService);
	private readonly domainService = inject(DomainService);
	private readonly productService = inject(ProductService);
	private readonly dataProviderFactory = inject(CompanyDataProviderFactory);

	constructor(private readonly brandService: BrandService) {
		this.brands = brandService.brands.map(b => new SelectListItemModel(b.id!, b.name));
		this.loadDomainProducts();
	}

	async loadDomainProducts() {
		const allProducts = await this.productService.getAllActiveProducts(false);
		const dkDomainProducts = allProducts.filter(p => p.productTags.some(pt => pt.tag == `${ProductPlanTagConstants.prefixForProductTags}${ProductPlanTagConstants.domainDk}`));
		const productVariants = this.productService.sortVariantsByPeriod(dkDomainProducts.flatMap(p => p.productVariants));
		this.availableVariants = productVariants.map(variant => new SelectListItemModel(variant, this.productService.getVariantName(variant)));
		this.selectedVariant = productVariants[0];
	}

	isSelected(domain: DomainWithDnsInformation) {
		return this.selectedDomains.some(d => d == domain);
	}

	toggleDomain(domain: DomainWithDnsInformation): void {
		const exists = this.selectedDomains.some(d => d.id === domain.id);
		this.selectedDomains = exists
			? this.selectedDomains.filter(d => d.id !== domain.id)
			: [...this.selectedDomains, domain];
	}

	canConfirm() {
		return this.selectedDomains.length > 0 && !!this.selectedContact && !!this.company && !!this.selectedVariant;
	}

	async confirm() {
		if (!this.company?.id || !this.selectedContact || !this.selectedVariant?.id || !this.brandId || this.selectedDomains.length == 0) return;
		await this.domainService.sendRegistrarManagementRequest(this.selectedDomains, this.selectedContact, this.selectedVariant.id, this.brandId, this.company);
		this.close();
	}

	close() {
		this.navigation.closeView(this);
	}
}

